import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Slider from 'rc-slider';
import { Link } from 'react-router-dom';
import { FaExclamationCircle } from 'react-icons/fa';

import { UseOutletContextService } from '../Service';
import { StateLanguage } from '../../../../../languages/config/StateLanguage';
import { Feature } from '../../../../../models/feature.model';
import { Container } from '../../../../../styles/container.style';
import { FeatureDetail } from '../../../../../models/feature.detail.model';
import { uploadTooltip } from '../../../../../utils/tooltip.util';
import { evaluateLegendOptionalIcon, evaluateLegendRequiredIcon, evaluateLegendObjectRequiredIcon } from '../../../../../scripts/validate.legend.script';
import { expressions } from '../../../../../libraries/regular.expressions.library';
import { legendInvalid, legendValid } from '../../../../../utils/legend.data.util';
import { uploadImg } from '../../../../../scripts/file.load.script';

import AppLegend from '../../../../../components/element/Legend';
import AppInventoryFormName from '../../component/InventoryFormName';
import AppInputMeasureUnit from '../../../../../components/element/InputMeasureUnit';
import AppInputTaxFee from '../../../../../components/element/InputTaxFee';
import AppInventoryFormGeneral from '../../component/InventoryFormGeneral';
import AppInventoryFormDetail from '../../component/InventoryFormDetail';
import AppInputFeatureOptions from '../../../../../components/element/InputFeatureOptions';
import imgDefault from '../../../../../assets/themes/default.png';

export interface AppServiceFormProps {
  action: string,
  loadIndicator: string,
  name: {value: string, valid: boolean},
  description: {value: string, valid: boolean},
  category: {value: string, valid: boolean, object: any},
  code: {value: string, valid: boolean},
  sku: {value: string, valid: boolean},
  codebar: {value: string, valid: boolean},
  priceSale: {value: string, valid: boolean},
  pricePurchase: {value: string, valid: boolean},
  taxCodeSale: {value: string, valid: boolean},
  taxCodePurchase: {value: string, valid: boolean},
  taxRateSale: {value: number, valid: boolean},
  taxRatePurchase: {value: number, valid: boolean},
  unit: {value: string, valid: boolean, object: any},
  discount: {value: {type: string, value: string, quantity: string}, valid: {type: boolean, value: boolean, quantity: boolean}},
  names: {value: {en: string, fr: string, it: string, de: string}, valid: {en: boolean, fr: boolean, it: boolean, de: boolean}},
  accounts: {value: {expense: string, vat: string, income: string}, valid: {expense: boolean, vat: boolean, income: boolean}},
  photos: {value: {name: string, size: number, type: string, view: string, file: any}[], valid: {gallery: boolean, list: {file: boolean, message: string}[]}},
  features: {value: FeatureDetail[], valid: boolean[]},
  tags: {value: string[], valid: boolean},
  setName: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setDescription: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCategory: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setCode: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setSku: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCodebar: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setPriceSale: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setPricePurchase: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setTaxCodeSale: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setTaxCodePurchase: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setTaxRateSale: Dispatch<SetStateAction<{value: number, valid: boolean}>>,
  setTaxRatePurchase: Dispatch<SetStateAction<{value: number, valid: boolean}>>,
  setUnit: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setDicount: Dispatch<SetStateAction<{value: {type: string, value: string, quantity: string}, valid: {type: boolean, value: boolean, quantity: boolean}}>>,
  setNames: Dispatch<SetStateAction<{value: {en: string, fr: string, it: string, de: string}, valid: {en: boolean, fr: boolean, it: boolean, de: boolean}}>>,
  setAccounts: Dispatch<SetStateAction<{value: {expense: string, vat: string, income: string}, valid: {expense: boolean, vat: boolean, income: boolean}}>>,
  setPhotos: Dispatch<SetStateAction<{value: {name: string, size: number, type: string, view: string, file: any}[], valid: {gallery: boolean, list: {file: boolean, message: string}[]}}>>
  setFeatures: Dispatch<SetStateAction<{value: FeatureDetail[], valid: boolean[]}>>,
  setTags: Dispatch<SetStateAction<{value: string[], valid: boolean}>>,
  submitService: Function
};

const AppServiceForm: React.FunctionComponent<AppServiceFormProps> = ({action, loadIndicator, name, description, category, code, sku, codebar, priceSale, pricePurchase, taxCodeSale, taxCodePurchase, taxRateSale, taxRatePurchase, unit, discount, names, accounts, photos, features, tags, setName, setDescription, setCategory, setCode, setSku, setCodebar, setPriceSale, setPricePurchase, setTaxCodeSale, setTaxCodePurchase, setTaxRateSale, setTaxRatePurchase, setUnit, setDicount, setNames, setAccounts, setPhotos, setFeatures, setTags, submitService}) => {
  const {companyForUser, settingsForCompany, categoriesForCompany, accountingExpenseAccountsForCompany, accountingVatAccountsForCompany, accountingIncomeAccountsForCompany, loadSettingsForCompany} = UseOutletContextService()
  const {lang} = StateLanguage()

  const [list, setList] = useState([] as Feature[])

  const handleChangeFiles = async (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    let archiveValueList: {name: string, size: number, type: string, view: string, file: any}[] = []
    let archiveValidList: {file: boolean, message: string}[] = []
    let archivesCapture: any = event.target.files

    for (let item of archivesCapture) {
      let archive = await uploadImg(lang, item)
      archiveValueList.push(archive.value)
      archiveValidList.push(archive.valid)
    }

    let tempPhotosValue = [...photos.value, ...archiveValueList]
    let tempPhotosValid = [...photos.valid.list, ...archiveValidList]
    let tempGalleryValid = (tempPhotosValid.length > 10) ? false : true

    if (tempGalleryValid) {
      legendInvalid('legend-gallery-max')
    } else {
      legendValid('legend-gallery-max')
    }

    setPhotos({value: tempPhotosValue, valid: {gallery: tempGalleryValid, list: tempPhotosValid}})
    cleanInputFile()
  }

  const handleChangeDescription = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setDescription({...description, value: event.target.value})
  }

  const handleChangeCode = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setCode({...code, value: event.target.value})
  }

  const handleChangeSku = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setSku({...sku, value: event.target.value})
  }

  const handleChangeCodebar = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setCodebar({...codebar, value: event.target.value})
  }

  const handleChangePriceSale = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setPriceSale({...priceSale, value: event.target.value})
  }

  const handleChangePricePurchase = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setPricePurchase({...pricePurchase, value: event.target.value})
  }

  const handleChangeDiscountType = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setDicount({value: {type: event.target.value, value: (event.target.value === 'percentage') ? '0' : '', quantity: ''}, valid: {type: true, value: (event.target.value === 'none'), quantity: (event.target.value === 'none')}})
  }

  const handleChangeInputDiscountValue = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setDicount({...discount, value: {...discount.value, value: event.target.value}})
  }

  const handleChangeInputDiscountQuantity = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setDicount({...discount, value: {...discount.value, quantity: event.target.value}})
  }

  const handleChangeSlideDiscountValue = (item: number | number[]) => {
    setDicount({...discount, value: {...discount.value, value: item.toString()}, valid: {...discount.valid, value: true}})
  }

  const executeRemoveFile = (index: number) => {
    let tempPhotosValue = photos.value.filter((_, temp_index) => (temp_index !== index))
    let tempPhotosValid = photos.valid.list.filter((_, temp_index) => (temp_index !== index))
    let tempGalleryValid = (tempPhotosValid.length > 10) ? false : true

    if (tempGalleryValid) {
      legendValid('legend-gallery-max')
    } else {
      legendInvalid('legend-gallery-max')
    }

    setPhotos({value: tempPhotosValue, valid: {gallery: tempGalleryValid, list: tempPhotosValid}})
    cleanInputFile()
  }

  const validateDescription = () => {
    evaluateLegendRequiredIcon(expressions.text, description, setDescription, 'input-description', 'legend-description-valid', 'legend-description-required')
  }

  const validateCode = () => {
    evaluateLegendOptionalIcon(expressions.code, code, setCode, 'input-code', 'legend-code-valid')
  }

  const validateSku = () => {
    evaluateLegendOptionalIcon(expressions.sku, sku, setSku, 'input-sku', 'legend-sku-valid')
  }

  const validateCodebar = () => {
    evaluateLegendOptionalIcon(expressions.codebar, codebar, setCodebar, 'input-codebar', 'legend-codebar-valid')
  }

  const validatePriceSale = () => {
    evaluateLegendRequiredIcon(expressions.currency, priceSale, setPriceSale, 'input-price-sale', 'legend-price-sale-valid', 'legend-price-sale-required')
  }

  const validatePricePurchase = () => {
    evaluateLegendRequiredIcon(expressions.currency, pricePurchase, setPricePurchase, 'input-price-purchase', 'legend-price-purchase-valid', 'legend-price-purchase-required')
  }

  const validateDiscountValue = () => {
    evaluateLegendObjectRequiredIcon(expressions.currency, 'value', discount, setDicount, 'input-discount-value', 'legend-discount-value-valid', 'legend-discount-value-required')
  }

  const validateDiscountQuantity = () => {
    evaluateLegendObjectRequiredIcon(expressions.quantity, 'quantity', discount, setDicount, 'input-discount-quantity', 'legend-discount-quantity-valid', 'legend-discount-quantity-required')
  }

  function cleanInputFile() {
    Array.from(document.querySelectorAll(`input[name="${'photos'}"]`)).forEach( (node)  => {
      let element = node as HTMLInputElement
      element.value = ''
    })
  }

  useEffect( () => {
    uploadTooltip()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, settingsForCompany, categoriesForCompany])

  return (
    <>
      { companyForUser && settingsForCompany && categoriesForCompany && accountingExpenseAccountsForCompany && accountingVatAccountsForCompany && accountingIncomeAccountsForCompany &&
        <div className="form d-flex flex-column flex-xl-row">
          <div className="d-flex flex-column flex-xl-row-auto gap-5 w-100 w-xl-350px mb-5 mb-xl-0">
            <AppInventoryFormGeneral inventory={"service"} action={action} display={"d-block d-xl-none"} other={false} name={name} tags={tags} setName={setName} setTags={setTags}></AppInventoryFormGeneral>
            <AppInventoryFormName action={action} names={names} setNames={setNames}></AppInventoryFormName>
            <AppInventoryFormDetail inventory={"service"} action={action} category={category} accounts={accounts} list={list} features={features} setCategory={setCategory} setAccounts={setAccounts} setList={setList} setFeatures={setFeatures}></AppInventoryFormDetail>
          </div>
          <div className="d-flex flex-column flex-row-fluid gap-5 ms-xl-5">
            <div className="d-flex flex-column gap-5">
              <AppInventoryFormGeneral inventory={"service"} action={action} display={"d-none d-xl-block"} other={true} name={name} tags={tags} setName={setName} setTags={setTags}></AppInventoryFormGeneral>
              <div className="card card-flush">
                <div className="card-header">
                  <div className="card-title">
                    <h3>{lang.labels.advanced}</h3>
                  </div>
                </div>
                <div className="card-body pt-0">
                  <div className="mb-3">
                    <label className="form-label required">{lang.labels.description}</label>
                    <input id="input-description" className="form-control" type="text" autoComplete="off" name="description" value={description.value} onChange={handleChangeDescription} onKeyUp={validateDescription} onBlur={validateDescription} />
                    <AppLegend attribute={{check: description.valid, name: "description", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                  <div className="d-flex flex-wrap gap-3 mb-3">
                    <div className="fv-row w-100 flex-md-root">
                      <label className="form-label">
                      <div className="d-flex align-items-center">
                        {lang.labels.code}
                        <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.uniqueServiceCodeAssignedByCompany}><FaExclamationCircle /></i>
                      </div>
                    </label>
                    <input id="input-code" className="form-control" type="text" autoComplete="off" name="code" value={code.value} onChange={handleChangeCode} onKeyUp={validateCode} onBlur={validateCode} />
                    <AppLegend attribute={{check: code.valid, name: "code", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                    </div>
                    <div className="fv-row w-100 flex-md-root">
                      <label className="form-label">{lang.labels.sku}</label>
                      <input id="input-sku" className="form-control" type="text" autoComplete="off" name="sku" value={sku.value} onChange={handleChangeSku} onKeyUp={validateSku} onBlur={validateSku} />
                      <AppLegend attribute={{check: sku.valid, name: "sku", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                    </div>
                    <div className="fv-row w-100 flex-md-root">
                      <label className="form-label">{lang.labels.codebar}</label>
                      <input id="input-codebar" className="form-control" type="text" autoComplete="off" name="codebar" value={codebar.value} onChange={handleChangeCodebar} onKeyUp={validateCodebar} onBlur={validateCodebar} />
                      <AppLegend attribute={{check: codebar.valid, name: "codebar", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label required">{lang.labels.unitMeasure}</label>
                    <AppInputMeasureUnit options={{type: "service", main_modal: null, index: null, sub_index: null}} companyForUser={companyForUser} settingsForCompany={settingsForCompany} measureUnit={unit} setMeasureUnit={setUnit} loadSettingsForCompany={loadSettingsForCompany}></AppInputMeasureUnit>
                  </div>
                  <div className="fv-row mb-3">
                    <div className="row row-cols-1 row-cols-md-2 g-3">
                      <div className="col">
                        <label className="form-label required">{lang.labels.priceSale}</label>
                        <input id="input-price-sale" className="form-control" type="text" autoComplete="off" name="price" value={priceSale.value} onChange={handleChangePriceSale} onKeyUp={validatePriceSale} onBlur={validatePriceSale} />
                        <AppLegend attribute={{check: priceSale.valid, name: "price-sale", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                      </div>
                      <div className="col">
                        <div className="fv-row w-100 flex-md-root">
                          <label className="form-label required">{lang.labels.taxSale}</label>
                          <AppInputTaxFee options={{main_modal: null, index: null, sub_index: null, type: "sale"}} companyForUser={companyForUser} settingsForCompany={settingsForCompany} taxCode={taxCodeSale} taxRate={taxRateSale} setTaxCode={setTaxCodeSale} setTaxRate={setTaxRateSale}></AppInputTaxFee>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="fv-row mb-3">
                    <div className="row row-cols-1 row-cols-md-2 g-3">
                      <div className="col">
                        <label className="form-label required">{lang.labels.pricePurchase}</label>
                        <input id="input-price-purchase" className="form-control" type="text" autoComplete="off" name="price" value={pricePurchase.value} onChange={handleChangePricePurchase} onKeyUp={validatePricePurchase} onBlur={validatePricePurchase} />
                        <AppLegend attribute={{check: pricePurchase.valid, name: "price-purchase", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                      </div>
                      <div className="col">
                        <div className="fv-row w-100 flex-md-root">
                          <label className="form-label required">{lang.labels.taxPurchase}</label>
                          <AppInputTaxFee options={{main_modal: null, index: null, sub_index: null, type: "purchase"}} companyForUser={companyForUser} settingsForCompany={settingsForCompany} taxCode={taxCodePurchase} taxRate={taxRatePurchase} setTaxCode={setTaxCodePurchase} setTaxRate={setTaxRatePurchase}></AppInputTaxFee>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Container property={(features.value.length > 0).toString()}>
                    <label className="form-label required">{lang.labels.features}</label>
                    { features.value.map (( (item, index) => { return (
                      <div key={index} className="row mb-3">
                        <label className="col-lg-4 col-form-label d-flex align-items-center">
                          <span className="bullet bg-primary me-2"></span>
                          {item.name}
                        </label>
                        <div className="col-lg-8">
                          <AppInputFeatureOptions options={{index: index}} settingsForCompany={settingsForCompany} features={features} setFeatures={setFeatures}></AppInputFeatureOptions>
                        </div>
                      </div>
                    )}))}
                  </Container>
                  <div className="mb-5">
                    <label className="form-label">
                      <div className="d-flex align-items-center">
                        {lang.labels.discount}
                        <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.uniqueServiceCodeAssignedByCompany}><FaExclamationCircle /></i>
                      </div>
                    </label>
                    <div className="fv-row mb-5">
                      <div className="row row-cols-1 row-cols-md-3 g-2">
                        <div className="col">
                          <label className={`btn btn-outline btn-outline-dashed btn-active-light-primary d-flex text-start px-2 py-3 ${discount.value.type === "none" && "active"}`}>
                            <span className="form-check form-check-custom form-check-solid form-check-sm align-items-center justify-content-center">
                              <input className="form-check-input" type="radio" value="none" checked={discount.value.type === "none"} onChange={handleChangeDiscountType} />
                            </span>
                            <span className="ms-2 fs-6 fw-bold d-block text-nowrap">{lang.labels.noDiscount}</span>
                          </label>
                        </div>
                        <div className="col">
                          <label className={`btn btn-outline btn-outline-dashed btn-active-light-primary d-flex text-start px-2 py-3 ${discount.value.type === "percentage" && "active"}`}>
                            <span className="form-check form-check-custom form-check-solid form-check-sm align-items-center justify-content-center">
                              <input className="form-check-input" type="radio" value="percentage" checked={discount.value.type === "percentage"} onChange={handleChangeDiscountType} />
                            </span>
                            <span className="ms-2 fs-6 fw-bold d-block text-nowrap">{lang.labels.percentage}</span>
                          </label>
                        </div>
                        <div className="col">
                          <label className={`btn btn-outline btn-outline-dashed btn-active-light-primary d-flex text-start px-2 py-3 ${discount.value.type === "fixed" && "active"}`}>
                            <span className="form-check form-check-custom form-check-solid form-check-sm align-items-center justify-content-center">
                              <input className="form-check-input" type="radio" value="fixed" checked={discount.value.type === "fixed"}  onChange={handleChangeDiscountType} />
                            </span>
                            <span className="ms-2 fs-6 fw-bold d-block text-nowrap">{lang.labels.fixedAmount}</span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <Container property={(discount.value.type === "percentage").toString()}>
                      <div className="mb-5">
                        <label className="form-label required">{lang.labels.setDiscountPercentage}</label>
                        <div className="d-flex flex-column text-center">
                          <div className="d-flex align-items-start justify-content-center text-nowrap">
                            <span className="fw-bolder fs-1">{discount.value.value}</span>
                            <span className="fw-bold fs-7 ms-1 mt-1">%</span>
                          </div>
                          <Slider value={Number(discount.value.value)} defaultValue={Number(discount.value.value)} onChange={handleChangeSlideDiscountValue} onChangeComplete={handleChangeSlideDiscountValue} step={1} min={0} max={100} styles={{track: {height: 8, backgroundColor: "gray"}, rail: {height: 8, backgroundColor: "silver"}, handle: {height: 18, width: 18, opacity: 100, borderColor: "blue", backgroundColor: "black"}}} />
                        </div>
                      </div>
                    </Container>
                    <Container property={(discount.value.type === "fixed").toString()}>
                      <div className="mb-3">
                        <label className="form-label required">{lang.labels.fixedDiscountedAmount}</label>
                        <input id="input-discount-value" className="form-control" type="text" autoComplete="off" name="discount-value" value={discount.value.value} onChange={handleChangeInputDiscountValue} onKeyUp={validateDiscountValue} onBlur={validateDiscountValue} />
                        <AppLegend attribute={{check: discount.valid.value, name: "discount-value", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                      </div>
                    </Container>
                    <Container property={(discount.value.type === "percentage" || discount.value.type === "fixed").toString()}>
                      <div className="mb-0">
                        <label className="form-label required">{lang.labels.quantityMinimumDiscount}</label>
                        <input id="input-discount-quantity" className="form-control" type="text" autoComplete="off" name="discount-quantity" value={discount.value.quantity} onChange={handleChangeInputDiscountQuantity} onKeyUp={validateDiscountQuantity} onBlur={validateDiscountQuantity} />
                        <AppLegend attribute={{check: discount.valid.quantity, name: "discount-quantity", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                      </div>
                    </Container>
                  </div>
                  <div className="mb-0">
                    <div className="dropzone dz-clickable position-relative">
                      <input className="position-absolute opacity-0 w-100 h-100 top-0 bottom-0 start-0 end-0 cursor-pointer" type="file" name={"photos"} accept=".png, .jpg, .jpeg" multiple onChange={handleChangeFiles} />
                      { photos.value.length > 0 && photos.valid.list.length > 0 && photos.value.length === photos.valid.list.length
                        ?
                        <>
                          { photos.value.map (( (item, index) => { return (
                            <div key={index} className="dz-preview dz-file-preview dz-error dz-complete">
                              <div className="dz-image">
                                <img src={photos.value[index].file ? item.view : imgDefault} className="w-100 h-100" alt={(item.name + index)} />
                              </div>
                              <div className="dz-details">
                                <div className="dz-size">
                                  <span>{item.size} {lang.labels.mb}</span>
                                </div>
                                <div className="dz-filename">
                                  <span className="text-uppercase">{item.type}</span>
                                </div>
                              </div>
                              { photos.value[index].file
                                ?
                                <div className="dz-success-mark">
                                  <svg viewBox="0 0 55 55" version="1.1" xmlns="http://www.w3.org/2000/svg">
                                    <title>{lang.labels.success}</title>
                                    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                      <path d="M23.5,31.8431458 L17.5852419,25.9283877 C16.0248253,24.3679711 13.4910294,24.366835 11.9289322,25.9289322 C10.3700136,27.4878508 10.3665912,30.0234455 11.9283877,31.5852419 L20.4147581,40.0716123 C20.5133999,40.1702541 20.6159315,40.2626649 20.7218615,40.3488435 C22.2835669,41.8725651 24.794234,41.8626202 26.3461564,40.3106978 L43.3106978,23.3461564 C44.8771021,21.7797521 44.8758057,19.2483887 43.3137085,17.6862915 C41.7547899,16.1273729 39.2176035,16.1255422 37.6538436,17.6893022 L23.5,31.8431458 Z M27,53 C41.3594035,53 53,41.3594035 53,27 C53,12.6405965 41.3594035,1 27,1 C12.6405965,1 1,12.6405965 1,27 C1,41.3594035 12.6405965,53 27,53 Z" strokeOpacity="0.198794158" stroke="#747474" fillOpacity="0.816519475" fill="#FFFFFF"></path>
                                    </g>
                                  </svg>
                                </div>
                                :
                                <div className="dz-error-mark">
                                  <svg viewBox="0 0 55 55" version="1.1" xmlns="http://www.w3.org/2000/svg">
                                    <title>{lang.labels.error}</title>
                                    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                      <g stroke="#7E8299" strokeOpacity="0.198794158" fill="#a92222" fillOpacity="0.816519475">
                                        <path d="M32.6568542,29 L38.3106978,23.3461564 C39.8771021,21.7797521 39.8758057,19.2483887 38.3137085,17.6862915 C36.7547899,16.1273729 34.2176035,16.1255422 32.6538436,17.6893022 L27,23.3431458 L21.3461564,17.6893022 C19.7823965,16.1255422 17.2452101,16.1273729 15.6862915,17.6862915 C14.1241943,19.2483887 14.1228979,21.7797521 15.6893022,23.3461564 L21.3431458,29 L15.6893022,34.6538436 C14.1228979,36.2202479 14.1241943,38.7516113 15.6862915,40.3137085 C17.2452101,41.8726271 19.7823965,41.8744578 21.3461564,40.3106978 L27,34.6568542 L32.6538436,40.3106978 C34.2176035,41.8744578 36.7547899,41.8726271 38.3137085,40.3137085 C39.8758057,38.7516113 39.8771021,36.2202479 38.3106978,34.6538436 L32.6568542,29 Z M27,53 C41.3594035,53 53,41.3594035 53,27 C53,12.6405965 41.3594035,1 27,1 C12.6405965,1 1,12.6405965 1,27 C1,41.3594035 12.6405965,53 27,53 Z"></path>
                                      </g>
                                    </g>
                                  </svg>
                                </div>
                              }
                              { !photos.valid.list[index].file &&
                                <div className="dz-error-message">
                                  <span className="text-justify">{photos.valid.list[index].message}</span>
                                </div>
                              }
                              <button className="dz-remove" type="button" onClick={() => executeRemoveFile(index)}>{lang.labels.remove}</button>
                            </div>
                          )}))}
                        </>
                        :
                        <div className="dz-message">
                          <span className="svg-icon svg-icon-primary svg-icon-3x">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path opacity="0.3" d="M5 16C3.3 16 2 14.7 2 13C2 11.3 3.3 10 5 10H5.1C5 9.7 5 9.3 5 9C5 6.2 7.2 4 10 4C11.9 4 13.5 5 14.3 6.5C14.8 6.2 15.4 6 16 6C17.7 6 19 7.3 19 9C19 9.4 18.9 9.7 18.8 10C18.9 10 18.9 10 19 10C20.7 10 22 11.3 22 13C22 14.7 20.7 16 19 16H5ZM8 13.6H16L12.7 10.3C12.3 9.89999 11.7 9.89999 11.3 10.3L8 13.6Z" fill="black"/>
                              <path d="M11 13.6V19C11 19.6 11.4 20 12 20C12.6 20 13 19.6 13 19V13.6H11Z" fill="black"/>
                            </svg>
                          </span>
                          <div className="d-grid ms-5">
                            <span className="fs-6 fw-bold">{lang.labels.dropFilesHereOrClickUpload}</span>
                            <span className="fw-semibold text-muted">{lang.labels.uploadUpToFiles}</span>
                          </div>
                        </div>
                      }
                    </div>
                    <div className="form-text text-justify">{lang.labels.setServiceMediaGallery}</div>
                    <div className={`fv-plugins-message-container invalid-feedback ${photos.valid.gallery && "d-none"}`}>
                      <div data-field="document" data-validator="file">{lang.labels.limitFilesMaximum}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <Link to="/main/inventory/service/list" className="btn btn-lg btn-light fw-bolder text-uppercase me-2">{lang.labels.cancel}</Link>
              <button className="btn btn-lg btn-primary fw-bolder w-100 w-md-300px" type="button" data-kt-indicator={loadIndicator} onClick={() => submitService()}>
                <span className="indicator-label text-uppercase text-nowrap">{action === "create" ? lang.labels.createService : lang.labels.updateService}</span>
                <span className="indicator-progress">
                {lang.labels.pleaseWait}
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              </button>
            </div>
          </div>
        </div>
      }
    </>
  )
};

export default AppServiceForm;
